import { useLazyQuery } from '@apollo/client'
import {
  Card,
  CardContent,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { isEmpty, round } from 'lodash'
import React, { useEffect } from 'react'

import { useRefresh } from '@/contexts/RefreshContext'
import {
  ActionType,
  GET_STORE_ACTION_COMPLETION,
  GetStoreActionCompletionQuery,
  GetStoreActionCompletionQueryVariables,
  SortDirection,
} from '@/graphql'
import useT from '@/i18n/useT'

import { StoreHeaderWithQuery } from '../stores/StoreHeaderWithQuery'

type Props = {
  startTime?: string
  endTime?: string
  storeIds?: string[]
  rankEnd: 'top' | 'bottom'
  actionTypes?: ActionType[]
  isRestock?: boolean
  actionShiftIds?: string[]
  onRowClicked: (storeId: string) => void
}

export const StoreTaskRank: React.FC<Props> = props => {
  const { t } = useT()
  const {
    startTime,
    endTime,
    storeIds,
    rankEnd,
    actionTypes,
    isRestock,
    actionShiftIds,
    onRowClicked,
  } = props

  const [
    getStoreActionCompletion,
    { data: actionCompletionData, loading, refetch, variables },
  ] = useLazyQuery<
    GetStoreActionCompletionQuery,
    GetStoreActionCompletionQueryVariables
  >(GET_STORE_ACTION_COMPLETION, { notifyOnNetworkStatusChange: true })

  useRefresh(() => {
    if (!isEmpty(variables)) {
      refetch()
    }
  })

  const stores = actionCompletionData?.storeActionCompletion?.results
  const highlightedStores = stores?.slice(0, 3) || []

  useEffect(() => {
    if (startTime && endTime) {
      getStoreActionCompletion({
        variables: {
          filters: {
            startTime,
            endTime,
            storeIds,
            actionTypes,
            isRestock,
            actionShiftIds,
          },
          order: rankEnd === 'top' ? SortDirection.DESC : SortDirection.ASC,
        },
      })
    }
  }, [
    startTime,
    endTime,
    isRestock,
    JSON.stringify(storeIds),
    JSON.stringify(actionTypes),
    JSON.stringify(actionShiftIds),
  ])

  return (
    <Card sx={{ flex: 1 }}>
      <CardContent>
        {loading ? (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        ) : (
          <>
            <Typography variant="h6" mb={4}>
              {rankEnd === 'top'
                ? t('rankedStores.topStoresTitle')
                : t('rankedStores.bottomStoresTitle')}
            </Typography>
            <Stack direction={'column'} spacing={1}>
              {highlightedStores.length > 0 ? (
                highlightedStores.map(store => (
                  <Link
                    color={'textPrimary'}
                    sx={{ cursor: 'pointer' }}
                    key={store.store.id}
                    onClick={() => {
                      onRowClicked(store.store.id)
                    }}
                  >
                    <Stack
                      direction={'row'}
                      spacing={4}
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Stack
                        direction={'row'}
                        spacing={3}
                        sx={{ alignItems: 'center' }}
                      >
                        <Typography variant="subtitle1">
                          {store.rank}
                        </Typography>
                        <StoreHeaderWithQuery
                          id={store.store.id}
                          singleRow
                          noZip
                        />
                      </Stack>
                      <Typography key={store.store.id} variant="body2">
                        {round(
                          store.tasksCompletionPercentage * 100,
                        ).toLocaleString()}
                        %
                      </Typography>
                    </Stack>
                  </Link>
                ))
              ) : (
                <Typography variant="body2">
                  {t('rankedStores.noData')}
                </Typography>
              )}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  )
}
